@import './fluid-button.theme.less';

@import '../../styles/mixins/fluid-transitions.mixin.less';
@import '../../styles/mixins/_color.less';

// ================================================================ //
// -- Theme Definition

:host {
  display: inline-block;
  vertical-align: middle;

  *, ::after, ::before {
    box-sizing: border-box;
  }

  .fluid-btn {

    /**
     * LEGACY THEME
     */
    &.legacy {
      @import '../../styles/variables-iuw-legacy.less';
      @button-tokens: @button-tokens-legacy;
      @button();
    }

    /**
     * LIBERTY MUTUAL THEME
     */
    &.lm {
      @import '../../styles/variables-lm.less';
      @button-tokens: @button-tokens-lm;
      @button();
    }

    /**
     * LM CORPORATE THEME
     */
    &.corp, &.default {
      @import '../../styles/variables-lm-corp.less';
      @button-tokens: @button-tokens-corp;
      @button();
    }

    /**
     * PORTAL THEME
     */
    &.portal {
      @import '../../styles/variables-lm-portal.less';
      @button-tokens: @button-tokens-portal;
      @button();
    }
  }
}


// ================================================================ //
// -- Component Detached Ruleset

@button: {
  @content: @button-tokens[@content];
  @primary: @button-tokens[@primary];
  @secondary: @button-tokens[@secondary];
  @tertiary: @button-tokens[@tertiary];
  @success: @button-tokens[@success];
  @inverse: @button-tokens[@inverse];
  @ghost: @button-tokens[@ghost];
  @ghost-padding-modifier: @button-tokens[@ghost-padding-modifier];
  @small: @button-tokens[@small];
  @large: @button-tokens[@large];
  @icon-button: @button-tokens[@icon-button];

  @ruleset--disabled: @button-tokens[@ruleset--disabled];

  @button-reset();

  min-width: @button-tokens[min-width];
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding-top: @button-tokens[padding-top];
  padding-bottom: @button-tokens[padding-bottom];
  padding-left: @button-tokens[padding-left];
  padding-right: @button-tokens[padding-right];
  text-align: center;
  text-decoration: none;
  font-family: @font-family;
  font-size: @button-tokens[font-size];
  font-weight: @button-tokens[font-weight];
  line-height: @button-tokens[line-height];
  margin-right: @button-tokens[margin-right];
  margin-bottom: @button-tokens[margin-bottom];
  border-width: @button-tokens[border-width];
  border-radius: @button-tokens[border-radius];
  border-style: @border-style;
  cursor: pointer;

  .transition(
    @property: background-color, border-color, color, box-shadow;
    @duration: short;
  );

  // ==== Focus & Active States (Base)

  &:focus {
    outline-offset: 4px;
  }

  &:active {
    transition: initial; // "Press" animation begins immediately
  }

  // ==== Primary Button

  &.primary {
    .create-button-type(@primary);
  }

  // ==== Secondary Button

  &.secondary {
    .create-button-type(@secondary);
  }

  // ==== Tertiary Button

  &.tertiary {
    .create-button-type(@tertiary);
  }

  // ==== Success Button

  &.success {
    .create-button-type(@success);
  }

  // ==== Inverse Button

  &.inverse {
    .create-button-type(@inverse);
  }

  // ==== Ghost Button

  &.ghost {
    .create-button-type(@ghost);

    font-weight: @font-weight-bold;
    padding-left: @button-tokens[padding-left] / @ghost-padding-modifier;
    padding-right: @button-tokens[padding-right] / @ghost-padding-modifier;
  }

  // ==== Ghost Button Light/Dark Color Scheme

  @button-color-scheme();

  // ==== Disabled State

  &.btn-disabled {
    @ruleset--disabled();
    cursor: not-allowed;
  }

  // ==== Remove Margins

  &.no-margin {
    margin-right: 0;
    margin-bottom: 0;
  }

  &.no-vertical-margin {
    margin-top: 0;
    margin-bottom: 0;
  }

  &.no-horizontal-margin {
    margin-left: 0;
    margin-right: 0;
  }

  // ==== Small Button

  &.btn-small:not(.icon-button) {
    min-width: @small[min-width];
    height: @small[min-height];
    line-height: @small[line-height];
    padding-top: 0;
    padding-bottom: 0;
    padding-left: @small[padding-left];
    padding-right: @small[padding-right];

    .btn-label {
      font-size: @small[font-size];
    }
  }

  // ==== Large Button

  &.btn-large:not(.icon-button) {
    min-width: @large[min-width];
    height: @large[min-height];
    line-height: @large[line-height];
    padding-top: 0;
    padding-bottom: 0;
    padding-left: @large[padding-left];
    padding-right: @large[padding-right];

    &.ghost {
      padding-left: @large[padding-left] / @ghost-padding-modifier;
      padding-right: @large[padding-right] / @ghost-padding-modifier;
    }

    .btn-label {
      font-size: @large[font-size];
    }
  }

  // ==== Aligned With Form Element

  &.form-element-height {
    height: @form-field-height;
  }


  // ============================================ //
  // -- Icon Button

  &.icon-only {
    min-width: unset;
    padding: 0;

    // == Button using "icon" Prop

    &.icon-button {
      border-width: 0;
      border-radius: 4em;
      color: @icon-button[color];
      fill: currentColor;

      &:not(.btn-disabled) {
        &:hover,
        &:focus-visible {
          background-color: @icon-button[@hover-background-color];
        }

        &:active {
          background-color: @icon-button[@active-background-color];
        }
      }
    }

    // == Light / Dark Color Scheme

    @button-color-scheme();

    // == Inherit Parent Color + Apply to Icon

    &.inherit-icon-color {
      color: unset;
      fill: currentColor;
    }

    // == Small Button

    &.btn-small {
      height: unset;
      min-width: unset;
      padding-left: 0;
      padding-right: 0;

      // Button with "iconBefore" / "iconAfter" & NO label
      &:not(.icon-button) {
        min-height: @small[min-height];
      }
    }

    // == Disabled Button

    &.btn-disabled {
      background-color: transparent;

      fluid-icon {
        color: @liberty-gray-29;
        cursor: not-allowed;
      }
    }

    // == Set Icon Sizes (Regular & Small Buttons)

    .create-icon-btn-sizes(); // Set predefined sizes

    // == Icon (both "icon" & "iconBefore" Props)

    fluid-icon {
      margin-left: 0;
      margin-right: 0;
      line-height: inherit;
      vertical-align: middle;

      & + fluid-icon {
        margin-left: @content[icon-after-margin];
      }
    }

    // == Slotted Icon-Only Button

    .icon-container {
      padding: 0.75em;
      margin: 0;
    }
  }

  // ==== Control Bar Buttons
  // If the button is part of a control bar, remove margins and set border-radius

  &.control-bar-start,
  &.control-bar-middle,
  &.control-bar-end {
    margin: 0;
    outline-width: 0;
  }

  &.control-bar-start {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    margin-right: -0.5px;
  }

  &.control-bar-middle {
    border-radius: 0;
    margin-right: -0.5px;
    margin-left: -0.5px;
  }

  &.control-bar-end {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: -0.5px;
  }

  // ==== Button Label

  .btn-label {
    font-size: @content[label-font-size];
    line-height: inherit;
  }

  // ==== Icon (rendered via Prop and/or Slot)

  .icon-container,
  .icon-before,
  .icon-after {
    color: inherit;
    fill: currentColor;
  }

  .icon-container {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-before {
    margin-right: @content[icon-before-margin];
    line-height: inherit;
    vertical-align: middle;
  }

  .icon-after {
    margin-left: @content[icon-after-margin];
    line-height: inherit;
    vertical-align: middle;
  }
};


// ================================================================ //
// -- Utility Detached Ruleset(s)

@button-reset: {
  background-color: transparent;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
  border: 0;
  color: inherit;
  cursor: pointer;
};

/**
 * Creates classes for light/dark color schemes
 */
@button-color-scheme: {
  &.color-scheme-light {
    color: @color-text-inverse;
  }

  &.color-scheme-dark {
    color: @color-text;
  }

  &.color-scheme-light,
  &.color-scheme-dark {
    &:not(.btn-disabled) {
      &:hover,
      &:focus-visible,
      &:active {
        color: @color-text;
      }
    }
  }
}


// ================================================================ //
// -- Mixins

/**
 * Creates button type based on tokens defined for theme.
 * Sets :hover, :focus-visible, & :active states.
 */
.create-button-type(@btn-type) {
  @hover: @btn-type[@hover];
  @active: @btn-type[@active];

  .map-tokens-to-properties(@btn-type);

  &:not(.btn-disabled) {

    // == Focus & Hover States

    &:focus-visible,
    &:hover {
      .map-tokens-to-properties(@hover);

      &[class*="control-bar"] {
        border-color: @btn-type[border-color];
      }
    }

    // == Active (Pressed) State

    &:active {
      .map-tokens-to-properties(@active);
    }
  }
}

/**
 * Maps common Button theme tokens to their respective
 * CSS properties.
 */
.map-tokens-to-properties(@token-map) {
  background-color: @token-map[background-color];
  border-color: @token-map[border-color];
  color: @token-map[text-color];
  fill: currentColor;
  box-shadow: @token-map[box-shadow];
}

/**
 * Creates proportional "icon-only" Button sizes for each
 * supported Icon size. Sizes are based on accessibility
 * best practice for click target size.
 */
.create-icon-btn-sizes() {
  @icon-btn-sizes: {
    xxs: 32px;
    xs: 36px;
    sm: 40px;
    md: 48px;
    lg: 56px;
  }

  each(@icon-btn-sizes, {
    @size-value: @value;
    @small-size-value: @value - 12;

    fluid-icon {
      &.icon-@{key} {
        width: @size-value;
        height: @size-value;
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }
    }

    &.btn-small {
      fluid-icon {
        &.icon-@{key} {
          width: @small-size-value;
          height: @small-size-value;
        }
      }

      &:not(.icon-button) {
        fluid-icon {
          &.icon-@{key} {
            margin-left: (@small-size-value / (@index + 0.4));
            margin-right: (@small-size-value / (@index + 0.4));
          }
        }
      }
    }
  });
}
